import { forwardRef, type SelectHTMLAttributes, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Estado de erro: borda destructive + ring vermelho no focus. */
  hasError?: boolean;
}

/**
 * Select nativo com estilo do design system. Preferido em listas pequenas
 * (< 10 opções) por: (1) acessibilidade nativa em mobile (bottom-sheet
 * do iOS/Android), (2) autofill + keyboard já funcionam, (3) bundle
 * menor que Radix Select. Para listas longas/buscáveis, migrar para
 * Radix Select em componente separado.
 *
 * @example
 * <Select value={value} onChange={e => setValue(e.target.value)}>
 *   <option value="">Selecione…</option>
 *   <option value="a">Opção A</option>
 * </Select>
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, hasError, children, ...props },
  ref,
): ReactElement {
  return (
    <select
      ref={ref}
      aria-invalid={hasError === true ? true : undefined}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background md:text-sm',
        'appearance-none bg-[length:1rem_1rem] bg-no-repeat bg-[right_0.75rem_center] pr-10',
        // caret SVG inline (evita depender de lib de ícones no primitivo)
        "bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2216%22%20height=%2216%22%20viewBox=%220%200%2024%2024%22%20fill=%22none%22%20stroke=%22currentColor%22%20stroke-width=%222%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22><polyline%20points=%226%209%2012%2015%2018%209%22/></svg>')]",
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        hasError === true && 'border-destructive focus-visible:ring-destructive',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
});
